import React, { useState, useEffect } from "react";
import { X, ChevronDown, Edit } from "lucide-react";
import EditIcon from "../../Images/Edit_icon.svg";
import QusSwap from "../../Images/swap_qus.svg";
import QuizCreate from "./Quiz";
import axios from "axios";
import { API_URL, getAuthHeaders } from "../../config/api";
import ToastMessage from "../../config/ToastMessage";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const QuizPublisher = ({ isOpen, onClose, selectedLesson }) => {
  console.log("Schedule ID from QuizPublisher:", selectedLesson?.schedule_id);

  const [activeTab, setActiveTab] = useState("publish");
  const [releaseGrade, setReleaseGrade] = useState("finalSubmission");
  const [studentCanSee, setStudentCanSee] = useState({
    missedQuestions: true,
    correctAnswers: false,
    pointValue: false,
  });
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [selectedQuizQuestions, setSelectedQuizQuestions] = useState([]);
  const [toast, setToast] = useState({ type: "", message: "" });
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    quizId: null,
  });
  const [scheduleData, setScheduleData] = useState(null);

  // do not early-return before hooks; we'll check below before rendering JSX

  // fetch quizzes for the lesson
  const fetchQuizzes = async () => {
    const lessonId =
      selectedLesson?.lessonDbId ?? selectedLesson?.lesson_id ?? null;
    if (!lessonId) {
      setQuizzes([]);
      setSelectedQuiz(null);
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/quiz/${lessonId}`);
      if (res.data?.success) {
        // API may return array or single object
        const data = Array.isArray(res.data.data)
          ? res.data.data
          : [res.data.data];
        setQuizzes(data || []);
        setSelectedQuiz(data?.[0] ?? null);
      } else {
        setQuizzes([]);
        setSelectedQuiz(null);
      }
    } catch (err) {
      console.error("Quiz fetch error:", err);
      setQuizzes([]);
      setSelectedQuiz(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuizzes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLesson]);

  // keep selectedQuizQuestions in sync when quizzes or selectedQuiz changes
  useEffect(() => {
    if (selectedQuiz) {
      setSelectedQuizQuestions(selectedQuiz.questions || []);
    } else if (quizzes && quizzes.length) {
      setSelectedQuizQuestions(quizzes[0].questions || []);
    } else {
      setSelectedQuizQuestions([]);
    }
  }, [selectedQuiz, quizzes]);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handlePublish = async () => {
    if (!selectedQuiz || !selectedLesson) {
      setToast({ type: "error", message: "No quiz or lesson selected" });
      setTimeout(() => setToast({ type: "", message: "" }), 3000);
      return;
    }

    if (!startDate || !endDate) {
      setToast({ type: "error", message: "Please select start and end dates" });
      setTimeout(() => setToast({ type: "", message: "" }), 3000);
      return;
    }

    const studentCanSeeArray = [];
    if (studentCanSee.missedQuestions)
      studentCanSeeArray.push("missedQuestions");
    if (studentCanSee.correctAnswers) studentCanSeeArray.push("correctAnswers");
    if (studentCanSee.pointValue) studentCanSeeArray.push("pointValue");

    const releaseGradeText = {
      finalSubmission: "Show result after final submission",
      manualReview: "Show result after manual review",
      eachQuestion: "Show result immediately after each question",
    }[releaseGrade];

    const payload = {
      quiz_id: selectedQuiz.quiz_id || selectedQuiz.id,
      schedule_id: selectedLesson.schedule_id,
      assigned_by: null, // Will be set when user authentication is implemented
      release_grade: releaseGradeText,
      student_can_see: studentCanSeeArray.join(","),
      start_date: formatDate(startDate),
      end_date: formatDate(endDate),
      status: "ACTIVE",
    };

    try {
      const response = await axios.post(`${API_URL}/quiz/assign`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.data.success) {
        setToast({ type: "success", message: "quiz published successfully" });
        setTimeout(() => {
          setToast({ type: "", message: "" });
          onClose(); // Close the modal after success
        }, 3000);
      } else {
        throw new Error(response.data.message || "Failed to publish quiz");
      }
    } catch (error) {
      console.error("Publish error:", error);
      let errorMessage = "Failed to publish quiz. Please try again.";
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === "string") {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail
            .map((err) => err.msg || err.message || JSON.stringify(err))
            .join(", ");
        } else {
          errorMessage = JSON.stringify(error.response.data.detail);
        }
      }
      setToast({
        type: "error",
        message: errorMessage,
      });
      setTimeout(() => setToast({ type: "", message: "" }), 3000);
    }
  };
  // Add this useEffect after your existing useEffects
  useEffect(() => {
    const fetchScheduleData = async () => {
      if (!selectedLesson?.schedule_id) {
        console.log("No schedule_id available");
        return;
      }

      try {
        const response = await axios.get(
          `${API_URL}/course-schedules/${selectedLesson.schedule_id}`,
          { headers: getAuthHeaders() },
        );

        console.log("Schedule Data:", response.data);
        if (response.data?.success) {
          setScheduleData(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching schedule:", error);
      }
    };

    fetchScheduleData();
  }, [selectedLesson?.schedule_id]);

  // Reset formData when modal opens
  useEffect(() => {
    if (isOpen) {
      setStartDate("");
      setEndDate("");
      setSelectedBatch("");
    }
  }, [isOpen, selectedLesson?.schedule_id]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[1181px] bg-white rounded-xl shadow-2xl relative max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-9 py-5 border-b sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-bold text-gray-900">Quiz Publication</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5 text-gray-700" />
          </button>
        </div>

        <div className="p-8 pt-12">
          {/* Program Info Section */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg shadow-md border border-gray-200 p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <p className="text-sm text-gray-600 font-medium">Program</p>
                <p className="text-lg font-semibold text-zinc-800 mt-1">
                  {selectedLesson?.courseTitle}
                </p>

                <p className="text-sm text-gray-600 font-medium mt-6">
                  Module {selectedLesson?.moduleDisplayId ?? "-"}
                </p>
                <p className="text-lg font-semibold text-zinc-800 mt-1">
                  {selectedLesson?.moduleTitle || selectedLesson?.module || "—"}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-600 font-medium">
                  Lesson {selectedLesson?.lessonDisplayId ?? "-"}
                </p>
                <p className="text-lg font-semibold text-zinc-800 mt-1">
                  {selectedLesson?.lessonTitle || selectedLesson?.lesson || "—"}
                </p>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
            {/* Left Column - Settings */}
            <div className="lg:col-span-3 bg-white rounded-lg shadow-md border border-gray-200 p-5 space-y-6">
              <div className="text-left">
                <h3 className="flex items-start text-lg font-semibold text-zinc-800 mb-6">
                  Release Grade
                </h3>

                <label
                  className="flex items-center gap-3 mb-4 cursor-pointer group w-full"
                  onClick={() => setReleaseGrade("finalSubmission")}
                >
                  <div
                    className={`w-4 h-4 rounded flex items-center justify-center mt-0.5 flex-shrink-0 transition-all ${
                      releaseGrade === "finalSubmission"
                        ? "bg-green-500 scale-110"
                        : "bg-gray-200 group-hover:bg-gray-300"
                    } border shadow-sm`}
                  >
                    {releaseGrade === "finalSubmission" && (
                      <svg
                        className="w-2 h-1.5 text-white"
                        viewBox="0 0 8 6"
                        fill="none"
                      >
                        <path
                          d="M1 3L3 5L7 1"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <span className="text-sm text-zinc-600 group-hover:text-zinc-800 transition-colors">
                    Show result after final submission
                  </span>
                </label>

                <label
                  className="flex items-center gap-3 mb-4 cursor-pointer group w-full"
                  onClick={() => setReleaseGrade("manualReview")}
                >
                  <div
                    className={`w-4 h-4 rounded flex items-center justify-center mt-0.5 flex-shrink-0 transition-all ${
                      releaseGrade === "manualReview"
                        ? "bg-green-500 scale-110"
                        : "bg-gray-200 group-hover:bg-gray-300"
                    } border shadow-sm`}
                  >
                    {releaseGrade === "manualReview" && (
                      <svg
                        className="w-2 h-1.5 text-white"
                        viewBox="0 0 8 6"
                        fill="none"
                      >
                        <path
                          d="M1 3L3 5L7 1"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <span className="text-sm text-zinc-600 group-hover:text-zinc-800 transition-colors">
                    Show result after manual review
                  </span>
                </label>

                <label
                  className="flex items-center gap-3 mb-4 cursor-pointer group w-full"
                  onClick={() => setReleaseGrade("eachQuestion")}
                >
                  <div
                    className={`w-4 h-4 rounded flex items-center justify-center mt-0.5 flex-shrink-0 transition-all ${
                      releaseGrade === "eachQuestion"
                        ? "bg-green-500 scale-110"
                        : "bg-gray-200 group-hover:bg-gray-300"
                    } border shadow-sm`}
                  >
                    {releaseGrade === "eachQuestion" && (
                      <svg
                        className="w-2 h-1.5 text-white"
                        viewBox="0 0 8 6"
                        fill="none"
                      >
                        <path
                          d="M1 3L3 5L7 1"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <span className="text-sm text-zinc-600 group-hover:text-zinc-800 transition-colors">
                    Show result immediately after each question
                  </span>
                </label>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <h3 className="flex items-start text-lg font-semibold text-zinc-800 mb-6">
                  Student Can See
                </h3>

                <label
                  className="flex items-start gap-3 mb-5 cursor-pointer group"
                  onClick={() =>
                    setStudentCanSee((prev) => ({
                      ...prev,
                      missedQuestions: !prev.missedQuestions,
                    }))
                  }
                >
                  <div
                    className={`w-4 h-4 rounded flex items-center justify-center mt-0.5 flex-shrink-0 transition-all ${
                      studentCanSee.missedQuestions
                        ? "bg-green-500 scale-110"
                        : "bg-gray-200 group-hover:bg-gray-300"
                    } border shadow-sm`}
                  >
                    {studentCanSee.missedQuestions && (
                      <svg
                        className="w-2 h-1.5 text-white"
                        viewBox="0 0 8 6"
                        fill="none"
                      >
                        <path
                          d="M1 3L3 5L7 1"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <span className="text-sm text-zinc-600 group-hover:text-zinc-800 transition-colors">
                    Missed Questions
                  </span>
                </label>

                <label
                  className="flex items-start gap-3 mb-5 cursor-pointer group"
                  onClick={() =>
                    setStudentCanSee((prev) => ({
                      ...prev,
                      correctAnswers: !prev.correctAnswers,
                    }))
                  }
                >
                  <div
                    className={`w-4 h-4 rounded flex items-center justify-center mt-0.5 flex-shrink-0 transition-all ${
                      studentCanSee.correctAnswers
                        ? "bg-green-500 scale-110"
                        : "bg-gray-200 group-hover:bg-gray-300"
                    } border shadow-sm`}
                  >
                    {studentCanSee.correctAnswers && (
                      <svg
                        className="w-2 h-1.5 text-white"
                        viewBox="0 0 8 6"
                        fill="none"
                      >
                        <path
                          d="M1 3L3 5L7 1"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <span className="text-sm text-zinc-600 group-hover:text-zinc-800 transition-colors">
                    Correct Answers
                  </span>
                </label>

                <label
                  className="flex items-start gap-3 cursor-pointer group"
                  onClick={() =>
                    setStudentCanSee((prev) => ({
                      ...prev,
                      pointValue: !prev.pointValue,
                    }))
                  }
                >
                  <div
                    className={`w-4 h-4 rounded flex items-center justify-center mt-0.5 flex-shrink-0 transition-all ${
                      studentCanSee.pointValue
                        ? "bg-green-500 scale-110"
                        : "bg-gray-200 group-hover:bg-gray-300"
                    } border shadow-sm`}
                  >
                    {studentCanSee.pointValue && (
                      <svg
                        className="w-2 h-1.5 text-white"
                        viewBox="0 0 8 6"
                        fill="none"
                      >
                        <path
                          d="M1 3L3 5L7 1"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <span className="text-sm text-zinc-600 group-hover:text-zinc-800 transition-colors">
                    Point Value
                  </span>
                </label>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg shadow-sm border border-gray-200 p-5 mt-6">
                <h3 className="flex items-start text-lg font-semibold text-zinc-800 mb-4">
                  Quiz Details
                </h3>

                <p className="  text-sm mb-3 leading-6">
                  <span className=" text-zinc-600">Quiz Points: </span>
                  <span className="font-semibold text-gray-900">
                    {selectedQuiz?.total_points ?? "—"}
                  </span>
                </p>

                <p className=" text-sm mb-3 leading-6">
                  <span className="text-zinc-600">Quiz Minimum Points: </span>
                  <span className="font-bold text-gray-900">
                    {selectedQuiz?.passing_score ?? "—"}
                  </span>
                </p>

                <p className="text-sm gap-2 leading-6">
                  <span className="text-zinc-600">Overall Estimate Time: </span>
                  <span className="font-bold text-gray-900">
                    {(() => {
                      const timeLimit = selectedQuiz?.time_limit_seconds;
                      if (!timeLimit) return "—";
                      if (timeLimit < 3600) {
                        const minutes = Math.round(timeLimit / 60);
                        return `${minutes} min`;
                      } else {
                        const hours = Math.round(timeLimit / 3600);
                        return `${hours} hour${hours > 1 ? "s" : ""}`;
                      }
                    })()}
                  </span>
                </p>
              </div>
            </div>

            {/* Middle Column - Show Selected Quiz's Questions */}
            <div className="lg:col-span-4 bg-white rounded-lg shadow-md border border-gray-200 p-5 relative">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-lg font-semibold text-gray-900 leading-6">
                  {selectedLesson?.lessonTitle ||
                    selectedLesson?.lesson ||
                    "Lesson"}{" "}
                  Question
                </h3>
                <div className="flex gap-2">
                  <button className="w-10 h-10 bg-gradient-to-br from-zinc-50 to-zinc-100 rounded-full shadow-sm border border-stone-300 flex items-center justify-center hover:from-zinc-100 hover:to-zinc-200 transition-all hover:shadow-md">
                    <img
                      src={QusSwap}
                      alt="swap questions"
                      className="w-5 h-5 text-gray-900"
                    />
                  </button>
                  <button
                    onClick={() => {
                      if (selectedQuiz) {
                        setShowEditPopup(true);
                      }
                    }}
                    className="w-10 h-10 bg-gradient-to-br from-zinc-50 to-zinc-100 rounded-full shadow-sm border border-stone-300 flex items-center justify-center hover:from-zinc-100 hover:to-zinc-200 transition-all hover:shadow-md"
                  >
                    <img
                      src={EditIcon}
                      alt="edit question"
                      className="w-4 h-4 text-gray-700"
                    />
                  </button>
                </div>
              </div>

              <div className="space-y-3 max-h-[450px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {selectedQuiz &&
                selectedQuizQuestions &&
                selectedQuizQuestions.length > 0 ? (
                  selectedQuizQuestions.map((q, i) => (
                    <div
                      key={q.id || i}
                      className="rounded-lg shadow-sm border border-gray-200 p-3 mb-3"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="flex items-start text-sm font-medium text-gray-900">
                            {q.position ? `${q.position}. ` : ""}
                            {q.question_text ||
                              q.question ||
                              "Untitled question"}
                          </p>
                          <p className="flex items-start text-xs text-gray-500 mt-1">
                            {q.question_type || q.type || ""}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    <p className="text-sm">Select a quiz to view questions</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Quiz List */}
            <div className="lg:col-span-5 space-y-6">
              <div className="bg-white rounded-lg shadow-md border border-gray-200 p-5">
                <h3 className="text-lg font-semibold text-zinc-800 mb-4">
                  Question List
                </h3>

                {selectedQuiz && (
                  <div className="mb-4 pb-4 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-600">
                      Selected Quiz:
                    </p>
                    <p className="text-base font-bold text-gray-900 mt-1">
                      {selectedQuiz.title ||
                        selectedQuiz.quiz_title ||
                        "Untitled Quiz"}
                    </p>
                    {selectedQuizQuestions && (
                      <p className="text-xs text-gray-500 mt-2">
                        Total Questions: {selectedQuizQuestions.length}
                      </p>
                    )}
                  </div>
                )}

                {loading && <p className="text-sm text-gray-500">Loading...</p>}

                {!loading && quizzes.length === 0 && (
                  <div className="text-center space-y-3 py-8">
                    <p className="text-sm text-gray-500">
                      No quiz in this lesson
                    </p>
                    <button
                      onClick={() => {
                        setSelectedQuiz(null);
                        setShowEditPopup(true);
                      }}
                      className="px-4 py-2 bg-[#4AACC5] text-white rounded text-sm hover:bg-teal-500 transition"
                    >
                      + Add Quiz
                    </button>
                  </div>
                )}

                <div className="space-y-3">
                  {quizzes.map((q, idx) => (
                    <div
                      key={q.quiz_id ?? q.id ?? idx}
                      onClick={() => {
                        setSelectedQuiz(q);
                        setSelectedQuizQuestions(q.questions || []);
                      }}
                      className={`bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg shadow-sm border border-gray-200 p-3 flex items-center justify-between hover:shadow-md transition-all cursor-pointer ${
                        selectedQuiz &&
                        (selectedQuiz.quiz_id === q.quiz_id ||
                          selectedQuiz.id === q.id)
                          ? "ring-2 ring-[#4AACC5] bg-teal-50"
                          : ""
                      }`}
                    >
                      <div className="flex items-start gap-3 flex-1">
                        <div className="w-6 h-6 bg-white rounded border flex items-center justify-center flex-shrink-0 text-xs font-semibold text-zinc-800">
                          {idx + 1}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {q.title || q.quiz_title || "Untitled Quiz"}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {q.status || "Draft"}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Date Inputs */}
          <div className="flex flex-col md:flex-row gap-7 mb-6">
            {/* START DATE */}
            <div className="w-full md:w-72">
              <div className="flex items-center gap-1 mb-2">
                <label className="text-sm font-bold text-zinc-800">
                  Start Date
                </label>
                <span className="text-sm font-bold text-red-500">*</span>
              </div>

              <DatePicker
                selected={startDate}
                onChange={(date) => {
                  setStartDate(date);
                  setEndDate(null); // reset end date
                }}
                minDate={
                  scheduleData?.start_date
                    ? new Date(scheduleData.start_date) > new Date()
                      ? new Date(scheduleData.start_date)
                      : new Date()
                    : new Date()
                }
                maxDate={
                  scheduleData?.end_date
                    ? new Date(scheduleData.end_date)
                    : null
                }
                dateFormat="MM/dd/yyyy"
                placeholderText="Select Date"
                wrapperClassName="w-full block"
                className="w-full h-10 px-3 py-2 bg-white rounded-lg shadow-sm border border-zinc-200 text-sm text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#4AACC5]/20 focus:border-[#4AACC5] transition-all"
              />
            </div>

            {/* END DATE */}
            <div className="w-full md:w-72">
              <div className="flex items-center gap-1 mb-2">
                <label className="text-sm font-bold text-zinc-800">
                  End Date
                </label>
                <span className="text-sm font-bold text-red-500">*</span>
              </div>

              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                minDate={
                  startDate
                    ? new Date(
                        new Date(startDate).setDate(startDate.getDate() + 1),
                      )
                    : null
                }
                maxDate={
                  scheduleData?.end_date
                    ? new Date(scheduleData.end_date)
                    : null
                }
                dateFormat="MM/dd/yyyy"
                placeholderText="Select Date"
                wrapperClassName="w-full block"
                className={`w-full h-10 px-3 py-2 rounded-lg shadow-sm border text-sm text-zinc-600 transition-all
        ${
          !startDate
            ? "bg-gray-100 cursor-not-allowed border-zinc-200"
            : "bg-white border-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#4AACC5]/20 focus:border-[#4AACC5]"
        }`}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3.5 w-full sm:w-96">
            <button
              onClick={onClose}
              className="flex-1 h-11 px-6 rounded-lg border-2 border-[#4AACC5] flex items-center justify-center hover:bg-[#4AACC5]/5 transition-all hover:shadow-md"
            >
              <span className="text-base font-bold text-[#4AACC5]">Cancel</span>
            </button>
            <button
              onClick={handlePublish}
              className="flex-1 h-11 px-6 bg-gradient-to-r from-[#4AACC5] to-[#3d9bb0] rounded-lg border border-[#4AACC5] flex items-center justify-center hover:from-[#3d9bb0] hover:to-[#35879a] transition-all shadow-md hover:shadow-lg"
            >
              <span className="text-base font-bold text-white">Publish</span>
            </button>
          </div>
        </div>
      </div>
      {showEditPopup && (
        <QuizCreate
          isOpen={showEditPopup}
          onClose={() => setShowEditPopup(false)}
          selectedLesson={selectedLesson}
          quizData={selectedQuiz}
          publisherFormData={{ startDate, endDate, batch: selectedBatch }}
          onSave={async () => {
            // refresh list and close modal
            await fetchQuizzes();
            setShowEditPopup(false);
          }}
        />
      )}
      {/* Delete Confirmation Modal */}
      {deleteConfirm.isOpen && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Delete Quiz
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this quiz? This action cannot be
              undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() =>
                  setDeleteConfirm({ isOpen: false, quizId: null })
                }
                className="flex-1 h-10 px-4 rounded border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    await axios.delete(
                      `${API_URL}/quiz/${deleteConfirm.quizId}/delete`,
                    );
                    setToast({
                      type: "success",
                      message: "Quiz deleted successfully",
                    });
                    setDeleteConfirm({ isOpen: false, quizId: null });
                    fetchQuizzes();
                  } catch (err) {
                    console.error(err);
                    setToast({
                      type: "error",
                      message: "Failed to delete quiz",
                    });
                    setDeleteConfirm({ isOpen: false, quizId: null });
                  }
                }}
                className="flex-1 h-10 px-4 rounded bg-red-500 text-white font-bold hover:bg-red-600 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastMessage
        type={toast.type}
        message={toast.message}
        onClose={() => setToast({ type: "", message: "" })}
      />
    </div>
  );
};

export default QuizPublisher;
